import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";
import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { userState } from "@/lib/store";
import { formatTrialEnd, TRIAL_KEEP_OFFER_CODE, TRIAL_KEEP_OFFER_HREF } from "@/lib/trialFullAccess";

const DISMISSED_KEY = "trialFullAccessNoticeDismissed";

/**
 * Bottom-right card for accounts on the October 2026 full-access trial
 * (`user.trial_full_access`): says what they have, when it ends, and offers the
 * keep-your-access discount. Dismissable for the session (sessionStorage, so it
 * comes back on the next visit while the trial lasts). Disappears on its own the
 * moment the holder subscribes, because the backend stops sending the flag.
 */
const TrialFullAccessNotice = () => {
    const user = useRecoilValue(userState);
    const router = useRouter();
    const [dismissed, setDismissed] = useState(true);

    useEffect(() => {
        try {
            setDismissed(sessionStorage.getItem(DISMISSED_KEY) === "1");
        } catch {
            setDismissed(false);
        }
    }, []);

    if (!user?.trial_full_access || dismissed) return null;

    const dismiss = () => {
        try {
            sessionStorage.setItem(DISMISSED_KEY, "1");
        } catch {
            // storage unavailable: the card just stays for this page view
        }
        setDismissed(true);
    };

    return (
        <Paper
            elevation={6}
            role="status"
            sx={{
                position: "fixed",
                right: { xs: 8, sm: 24 },
                left: { xs: 8, sm: "auto" },
                bottom: { xs: 8, sm: 24 },
                zIndex: 1250,
                maxWidth: 420,
                p: 2,
                pr: 5,
                borderRadius: 2,
            }}
        >
            <IconButton size="small" aria-label="Dismiss" onClick={dismiss} sx={{ position: "absolute", top: 6, right: 6 }}>
                <CloseIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                You have full ORTEX access, free until {formatTrialEnd(user.trial_full_access_ends)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
                ORTEX Advanced, the Options Platform and Index Rebalance forecasts are all unlocked on your account. Subscribe before 1 November and keep ORTEX
                Advanced at 50% off for 6 months with code {TRIAL_KEEP_OFFER_CODE}.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button variant="contained" size="small" onClick={() => router.push(TRIAL_KEEP_OFFER_HREF)} sx={{ textTransform: "none", fontWeight: 600 }}>
                    Keep my access, 50% off
                </Button>
                <Button variant="text" size="small" onClick={dismiss} sx={{ textTransform: "none" }}>
                    Later
                </Button>
            </Box>
        </Paper>
    );
};

export default TrialFullAccessNotice;
